import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Search, Package, FileText, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatExpiryDate, parseExpiryDate } from '@/utils/dateUtils';

export const MedicineInventory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);

  // Notes Edit Dialog State (Only editable field in AyurClinic)
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // ERP Integration State
  const [syncingErp, setSyncingErp] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);

  const fetchInventory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/inventory');
      const data = response.data?.items || response.data || [];
      setMedicines(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleSyncWithErp = async () => {
    if (syncingErp) return;
    setSyncingErp(true);
    setSyncSummary(null);

    try {
      const res = await api.post('/erp/sync');
      const result = res.data?.data || res.data;

      setSyncSummary({
        created: result?.created ?? 0,
        updated: result?.updated ?? 0,
        unchanged: result?.unchanged ?? 0,
        fetched: result?.fetched ?? 0,
        synced_at: result?.synced_at || new Date().toISOString()
      });

      toast.success(
        `ERP Sync Complete: ${result?.created ?? 0} created, ${result?.updated ?? 0} updated, ${result?.unchanged ?? 0} unchanged (${result?.fetched ?? 0} total fetched)`
      );

      await fetchInventory();
      window.dispatchEvent(new CustomEvent('erp:health-check'));
    } catch (err) {
      console.error('ERP synchronization error:', err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        'ERP catalog synchronization failed. Local inventory was not modified.';
      toast.error(errMsg);
    } finally {
      setSyncingErp(false);
    }
  };

  const handleOpenNotesDialog = (medicine) => {
    setSelectedMedicine(medicine);
    setNotesValue(medicine.notes || '');
    setIsNotesOpen(true);
  };

  const handleSaveNotes = async (e) => {
    e.preventDefault();
    if (!selectedMedicine) return;
    setSavingNotes(true);
    try {
      await api.put(`/inventory/${selectedMedicine.id}`, { notes: notesValue });
      toast.success('Clinical notes updated successfully!');
      setIsNotesOpen(false);
      await fetchInventory();
    } catch (error) {
      console.error('Error saving notes:', error);
      const msg = error.response?.data?.detail || 'Failed to update notes';
      toast.error(msg);
    } finally {
      setSavingNotes(false);
    }
  };

  const filteredMedicines = medicines.filter(medicine =>
    (medicine.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (medicine.type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (medicine.notes || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatPrice = (value) => {
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return '—';
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(numeric);
  };

  const getExpiringCount = () => {
    const now = new Date();
    return medicines.filter((m) => {
      const d = parseExpiryDate(m.expiryDate);
      return d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Medicine Inventory</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Authoritative stock and pricing are managed in OneSpace ERP. Clinic staff maintain local clinical notes.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Sync with ERP Button */}
          <Button
            variant="outline"
            onClick={handleSyncWithErp}
            disabled={syncingErp || loading}
            data-testid="sync-erp-button"
            className="h-11 gap-1.5 border-emerald-300 text-emerald-800 hover:bg-emerald-50 font-medium w-full sm:w-auto"
            title="Synchronize medicine catalog and active batches from OneSpace ERP"
          >
            <RefreshCw className={`h-4 w-4 ${syncingErp ? 'animate-spin' : ''}`} />
            <span>{syncingErp ? 'Syncing...' : 'Sync with ERP'}</span>
          </Button>

          {/* Refresh Button */}
          <Button
            variant="outline"
            onClick={() => {
              fetchInventory();
              window.dispatchEvent(new CustomEvent('erp:health-check'));
            }}
            disabled={loading}
            className="hidden sm:inline-flex h-11 gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ERP Sync Summary Banner */}
      {syncSummary && (
        <div
          data-testid="sync-summary-banner"
          className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 flex items-center justify-between gap-3 animate-in fade-in"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <span>
              <strong>ERP Sync Complete ({new Date(syncSummary.synced_at).toLocaleTimeString()}):</strong>{' '}
              {syncSummary.created} created, {syncSummary.updated} updated, {syncSummary.unchanged} unchanged ({syncSummary.fetched} items fetched)
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100"
            onClick={() => setSyncSummary(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-6">
        <Card className="shadow-card min-w-0">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-1.5">
              <div className="min-w-0">
                <p className="text-[11px] sm:text-sm text-muted-foreground mb-0.5 sm:mb-1 truncate">Total Medicines</p>
                <p className="text-xl sm:text-3xl font-bold font-serif">{medicines.length}</p>
              </div>
              <div className="bg-primary/10 p-2 sm:p-3 rounded-lg flex-shrink-0">
                <Package className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card border-destructive/50 min-w-0">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-1.5">
              <div className="min-w-0">
                <p className="text-[11px] sm:text-sm text-muted-foreground mb-0.5 sm:mb-1 truncate">This Month Expiry</p>
                <p className="text-xl sm:text-3xl font-bold font-serif text-destructive">{getExpiringCount()}</p>
              </div>
              <div className="bg-destructive/10 p-2 sm:p-3 rounded-lg flex-shrink-0">
                <Package className="h-4 w-4 sm:h-6 sm:w-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hidden sm:block shadow-card bg-muted/50 min-w-0">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-1.5">
              <div className="min-w-0">
                <p className="text-[11px] sm:text-sm text-muted-foreground mb-0.5 sm:mb-1 truncate">Inventory Authority</p>
                <p className="text-sm sm:text-base font-medium truncate text-emerald-700">OneSpace ERP</p>
                <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5 truncate">Batch FEFO & Stock Ledger</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="shadow-card">
        <CardContent className="p-4 sm:p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by medicine name, type, or notes..."
              className="pl-10 h-11 text-sm sm:text-base"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Inventory List */}
      <Card className="shadow-card">
        <CardHeader className="border-b border-border py-4 px-4 sm:px-6">
          <CardTitle className="font-serif text-xl">Medicine Catalog ({filteredMedicines.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card List (< md) */}
          <div className="block md:hidden divide-y divide-border">
            {filteredMedicines.length > 0 ? (
              filteredMedicines.map((medicine) => (
                <div key={medicine.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-slate-900 text-base flex items-center gap-1.5">
                        <Package className="h-4 w-4 text-primary flex-shrink-0" />
                        {medicine.name}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {medicine.erp_product_id ? (
                          <span className="text-emerald-700 font-medium">
                            ERP #{medicine.erp_product_id} (Batch: {medicine.erp_batch_no || '—'})
                          </span>
                        ) : (
                          <span className="text-amber-600">Unlinked (Manual)</span>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      {medicine.type}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-xs bg-muted/40 p-2.5 rounded-lg text-center">
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Stock</span>
                      <span className={`font-bold text-sm ${parseInt(medicine.quantity, 10) < 10 ? 'text-destructive' : 'text-slate-800'}`}>
                        {medicine.quantity} {medicine.unit || ''}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[11px]">MRP</span>
                      <span className="font-medium text-slate-800">
                        {formatPrice(medicine.mrp)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Expiry</span>
                      <span className="font-medium text-slate-700">
                        {formatExpiryDate(medicine.expiryDate)}
                      </span>
                    </div>
                  </div>

                  {medicine.notes && (
                    <p className="text-xs text-muted-foreground italic bg-muted/20 px-2.5 py-1.5 rounded">
                      Note: {medicine.notes}
                    </p>
                  )}

                  <div className="pt-1">
                    <Button
                      variant="outline"
                      className="w-full h-11 text-sm font-medium gap-1.5"
                      onClick={() => handleOpenNotesDialog(medicine)}
                    >
                      <FileText className="h-4 w-4 text-primary" />
                      Edit Clinical Notes
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm p-4">
                No medicines found. Click "Sync with ERP" to synchronize the catalog.
              </div>
            )}
          </div>

          {/* Desktop Table (md+) */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Medicine Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Stock Quantity</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>MRP</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>ERP Mapping</TableHead>
                  <TableHead>Clinical Notes</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMedicines.length > 0 ? (
                  filteredMedicines.map((medicine) => (
                    <TableRow key={medicine.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="bg-primary/10 p-2 rounded-lg">
                            <Package className="h-4 w-4 text-primary" />
                          </div>
                          <span className="font-semibold text-foreground">{medicine.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{medicine.type}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className={parseInt(medicine.quantity, 10) < 10 ? 'text-destructive font-semibold' : 'font-medium'}>
                          {medicine.quantity}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">
                        {medicine.unit || '—'}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-foreground">{formatPrice(medicine.mrp)}</span>
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">
                        {formatExpiryDate(medicine.expiryDate)}
                      </TableCell>
                      <TableCell>
                        {medicine.erp_product_id ? (
                          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                            ERP #{medicine.erp_product_id} (Batch: {medicine.erp_batch_no || '—'})
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                            Manual
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs max-w-xs truncate" title={medicine.notes}>
                        {medicine.notes || '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenNotesDialog(medicine)}
                          title="Edit clinical notes"
                          className="gap-1.5 text-xs text-primary hover:text-primary/80"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          <span>Edit Notes</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No medicines found. Click "Sync with ERP" to synchronize the catalog.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Notes Dialog (ONLY editable field on AyurClinic) */}
      <Dialog open={isNotesOpen} onOpenChange={setIsNotesOpen}>
        <DialogContent className="max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl sm:text-2xl">
              Edit Clinical Notes
            </DialogTitle>
            <DialogDescription className="text-xs">
              Commercial inventory, stock quantities, and pricing are authoritatively managed in OneSpace ERP.
            </DialogDescription>
          </DialogHeader>

          {selectedMedicine && (
            <form onSubmit={handleSaveNotes} className="space-y-4 pt-2">
              {/* Read-Only ERP Product Details */}
              <div className="bg-muted/40 p-3 rounded-lg border border-border space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-foreground">{selectedMedicine.name}</span>
                  <Badge variant="outline">{selectedMedicine.type}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-muted-foreground pt-1 border-t border-border/50">
                  <div>
                    <span className="block text-[11px]">Stock in Clinic:</span>
                    <span className="font-medium text-foreground">
                      {selectedMedicine.quantity} {selectedMedicine.unit || 'units'}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[11px]">MRP:</span>
                    <span className="font-medium text-foreground">{formatPrice(selectedMedicine.mrp)}</span>
                  </div>
                  <div>
                    <span className="block text-[11px]">Expiry:</span>
                    <span className="font-medium text-foreground">{formatExpiryDate(selectedMedicine.expiryDate)}</span>
                  </div>
                  <div>
                    <span className="block text-[11px]">ERP Status:</span>
                    {selectedMedicine.erp_product_id ? (
                      <span className="font-medium text-emerald-600">
                        Product #{selectedMedicine.erp_product_id} (Batch: {selectedMedicine.erp_batch_no || '—'})
                      </span>
                    ) : (
                      <span className="font-medium text-amber-600">Unlinked</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Editable Notes Field */}
              <div className="space-y-2">
                <Label htmlFor="medicine-notes" className="text-sm font-medium">
                  Clinical / Dispensing Notes
                </Label>
                <Input
                  id="medicine-notes"
                  placeholder="e.g., Take after food with warm water"
                  className="h-11"
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  maxLength={500}
                  autoFocus
                />
                <p className="text-[11px] text-muted-foreground">
                  Notes are stored locally for doctor and pharmacy reference, and are never transmitted to ERP.
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:w-auto h-11"
                  onClick={() => setIsNotesOpen(false)}
                  disabled={savingNotes}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={savingNotes}
                  className="w-full sm:w-auto h-11"
                >
                  Save Notes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicineInventory;
